import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Button,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import { useQueryClient } from '@tanstack/react-query';
import {
  getIntroduction,
  addChapterToIntroduction,
  deleteIntroduction,
} from '../../../endpoints/introduction';
import { roles } from '../../../utils/variables';
import dayjs from 'dayjs';
import IntroductionForm from './IntroductionForm';
import ChapterSelector from '../../../Components/ChapterSelector';
import ConfirmationDialog from '../../../Components/ConfirmationDialog';
import AlertItem from '../../../Components/AlertItem';

const IntroductionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addChapterModalOpen, setAddChapterModalOpen] = useState(false);
  const [selectedChapterId, setSelectedChapterId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const {
    data: introduction,
    isLoading,
    error,
    refetch,
  } = useApiGet(['introduction', id], () => getIntroduction(id!), {
    enabled: !!id,
  });

  const { mutate: deleteIntro, isPending: isDeleting } = useApiSend(
    (introId: string) => deleteIntroduction(introId),
    () => {
      // Invalidate queries and navigate on success
      queryClient.invalidateQueries({ queryKey: ['introductions'] });
      navigate('/introductions');
    },
    (error: Error) => {
      setDeleteError(error.message || 'Failed to delete introduction');
      setShowDeleteConfirmation(false);
    }
  );

  const handleConfirmDelete = () => {
    if (!id) return;
    deleteIntro(id);
  };

  // Permission checking functions
  const canEdit = (): boolean => {
    const userRole = localStorage.getItem('role');
    const userId = localStorage.getItem('user_id');

    // Admin or Support can always edit
    if (userRole === roles.Admin || userRole === roles.Support) {
      return true;
    }

    // User can edit if they created the introduction
    return introduction?.created_by === userId;
  };

  const canAddChapter = (): boolean => {
    const userRole = localStorage.getItem('role');
    return userRole === roles.Admin || userRole === roles.Support;
  };

  const canDelete = (): boolean => {
    const userRole = localStorage.getItem('role');
    return userRole === roles.Admin || userRole === roles.Support;
  };

  const handleBack = () => {
    navigate('/introductions');
  };

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false);
    refetch();
  };

  const handleAddChapter = () => {
    setAddChapterModalOpen(true);
  };

  const handleAddChapterClose = () => {
    setAddChapterModalOpen(false);
    setSelectedChapterId('');
    setSubmitError(null);
  };

  const handleChapterSelect = (chapterId: string | null) => {
    setSelectedChapterId(chapterId || '');
  };

  const handleAddChapterSubmit = async () => {
    if (!selectedChapterId || !id) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await addChapterToIntroduction(id, { chapter_id: selectedChapterId });
      // Invalidate the introductions list cache to refetch the data
      queryClient.invalidateQueries({ queryKey: ['introductions'] });
      setAddChapterModalOpen(false);
      setSelectedChapterId('');
      refetch();
    } catch (error: any) {
      setSubmitError(error.message || 'Failed to add chapter');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => {
    setDeleteError(null);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setDeleteError(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error || !introduction) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Error loading introduction:{' '}
          {error?.message || 'Introduction not found'}
        </Typography>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Introductions
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/introductions"
          variant="body2"
          sx={{ cursor: 'pointer' }}
        >
          Non-Member Intro
        </Link>
        <Typography variant="body2" color="text.secondary">
          {introduction.first_name} {introduction.last_name}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Introduction Details
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {canEdit() && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              sx={{ backgroundColor: 'blue' }}
            >
              Edit
            </Button>
          )}
          {canDelete() && (
            <Button
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              color="error"
              disabled={isDeleting}
            >
              Delete
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back to List
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Personal Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  First Name
                </Typography>
                <Typography variant="body1">
                  {introduction.first_name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Last Name
                </Typography>
                <Typography variant="body1">
                  {introduction.last_name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{introduction.email}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Phone (Cell)
                </Typography>
                <Typography variant="body1">
                  {introduction.phone_cell || 'Not provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Phone (Home)
                </Typography>
                <Typography variant="body1">
                  {introduction.phone_home || 'Not provided'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Location Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Location Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Country
                </Typography>
                <Typography variant="body1">{introduction.country}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  State/Province
                </Typography>
                <Typography variant="body1">
                  {introduction.state || 'Not provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Postal Code
                </Typography>
                <Typography variant="body1">
                  {introduction.postal_code}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  City
                </Typography>
                <Typography variant="body1">
                  {introduction.city || 'Not provided'}
                </Typography>
              </Grid>
              {introduction.address_1 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Address Line 1
                  </Typography>
                  <Typography variant="body1">
                    {introduction.address_1}
                  </Typography>
                </Grid>
              )}
              {introduction.address_2 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Address Line 2
                  </Typography>
                  <Typography variant="body1">
                    {introduction.address_2}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              About This Person
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {introduction.description}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Chapters Contacted */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                Chapters Contacted
              </Typography>
              {canAddChapter() && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddChapter}
                  sx={{ backgroundColor: 'blue', color: 'white' }}
                >
                  Add Chapter
                </Button>
              )}
            </Box>
            {introduction.chapters && introduction.chapters.length > 0 ? (
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  The following chapters have been contacted about this
                  introduction:
                </Typography>
                {introduction.chapters.map((chapter) => (
                  <Box
                    key={chapter.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="medium">
                        {chapter.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {chapter.abbreviation}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {chapter.email_sent_on
                        ? `Email sent on ${dayjs(chapter.email_sent_on).format('MMM D, YYYY h:mm A')}`
                        : 'Email not yet sent'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No chapters have been contacted yet.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Metadata */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Submission Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Submitted By
                </Typography>
                <Typography variant="body1">
                  {introduction.created_by || 'Unknown'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Submitted On
                </Typography>
                <Typography variant="body1">
                  {introduction.created_at
                    ? dayjs(introduction.created_at).format('MM/DD/YYYY')
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body1">
                  {introduction.updated_at
                    ? dayjs(introduction.updated_at).format('MM/DD/YYYY')
                    : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleEditClose}
        maxWidth="lg"
        fullWidth
        fullScreen
      >
        <DialogContent sx={{ p: 0 }}>
          {introduction && (
            <IntroductionForm
              initialData={introduction}
              onSuccess={handleEditSuccess}
              onCancel={handleEditClose}
              isEdit={true}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Chapter Modal */}
      <Dialog
        open={addChapterModalOpen}
        onClose={handleAddChapterClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Chapter to Introduction</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {submitError && (
              <Box sx={{ mb: 2 }}>
                <AlertItem
                  severity="error"
                  description={submitError}
                  onClose={() => setSubmitError(null)}
                  keepOpen={true}
                />
              </Box>
            )}
            <ChapterSelector
              chapter={selectedChapterId}
              handleChapterSelect={handleChapterSelect}
              label="Select Chapter"
              all={true}
              spd={null}
              reportId={id}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddChapterClose}>Cancel</Button>
          <Button
            onClick={handleAddChapterSubmit}
            variant="contained"
            disabled={!selectedChapterId || isSubmitting}
            sx={{ backgroundColor: 'blue' }}
          >
            {isSubmitting ? 'Adding...' : 'Add Chapter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={showDeleteConfirmation}
        handleCancel={handleDeleteCancel}
        handleOk={handleConfirmDelete}
        title="Delete Introduction"
        message="Are you sure you want to delete this introduction? This action cannot be undone. All related chapters will also be deleted."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Delete Error Alert */}
      {deleteError && (
        <Box sx={{ mt: 2 }}>
          <AlertItem
            severity="error"
            description={deleteError}
            onClose={() => setDeleteError(null)}
            keepOpen={true}
          />
        </Box>
      )}
    </Box>
  );
};

export default IntroductionDetail;
